import { type ReactNode } from "react"
import { Trans, useTranslation } from "react-i18next"
import { ArrowUpRight, ChevronsRight, CircleAlert, Eye } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { ACTIONS, stopActionLabel } from "@/reducers/routes2gSlice"
import {
  EditableOverviewStops,
  type RouteEditing,
} from "@/components/chat/EditableOverviewStops"
import {
  StopLabel,
  StopRowShell,
  StopStatus,
} from "@/components/chat/OverviewStopRow"
import { OrderDocumentList } from "@/components/chat/OrderDocumentList"
import {
  OrderStatusLink,
  type OrderStatus,
} from "@/components/chat/OrderStatusLink"
import type { OrderDocument } from "@/components/chat/useOrderDocuments"
import type {
  DriverOverview,
  OverviewStop,
  RouteView,
  Stage,
  StageDetail,
  WorkTimeView,
} from "@/lib/driverOverview"

const BREAK_COLOR = "#F97316"

const MaybeTooltip = ({
  tooltip,
  children,
}: {
  tooltip?: ReactNode
  children: ReactNode
}) =>
  tooltip ? (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{children}</span>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    <>{children}</>
  )

const BreakMarker = ({
  percent,
  tooltip,
  testId,
}: {
  percent: number
  tooltip: string
  testId: string
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-testid={testId}
          className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left: `${percent}%`, backgroundColor: BREAK_COLOR }}
          aria-label={tooltip}
        />
      </TooltipTrigger>
      <TooltipContent side="top">{tooltip}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const actionFor = (action: string | undefined) =>
  ACTIONS.find((option) => option.value === action?.toLowerCase()) ??
  ACTIONS.find((option) => option.value === "other")!

const RouteStopMarker = ({
  stop,
  percent,
}: {
  stop: OverviewStop
  percent: number
}) => {
  const { t } = useTranslation()
  const action = actionFor(stop.action)
  const type = stopActionLabel(t, action.value) ?? action.label
  const place = [stop.name, stop.address].filter(Boolean).join(" · ")
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid="driver-overview-stop-marker"
            className="absolute top-1/2 h-2.5 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3B82F6]"
            style={{ left: `${percent}%` }}
            aria-label={`${type}: ${place}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top">
          <span className="font-medium">{type}</span>{" "}
          <span className="opacity-80">· {place}</span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

const StopRow = ({ stop }: { stop: OverviewStop }) => {
  const Icon = actionFor(stop.action).icon
  return (
    <StopRowShell stop={stop}>
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1 truncate">
        <StopLabel stop={stop} />
      </span>
      <StopStatus stop={stop} />
    </StopRowShell>
  )
}

const StopList = ({ stops }: { stops: OverviewStop[] }) => {
  const { t } = useTranslation()
  return (
    <div className="flex min-h-0 flex-1 flex-col px-4 pb-3">
      {stops.length > 0 ? (
        <ol
          data-testid="driver-overview-stops"
          className="max-h-[126px] overflow-y-auto overflow-x-hidden"
        >
          {stops.map((stop) => (
            <StopRow key={stop.id} stop={stop} />
          ))}
        </ol>
      ) : (
        <p className="flex h-9 items-center pl-5 text-sm text-muted-foreground">
          {t("chat.driverOverview.noStops")}
        </p>
      )}
    </div>
  )
}

const RouteStatusBadge = ({ status }: { status: RouteView["status"] }) => {
  const { t } = useTranslation()
  return (
    status && (
      <Badge
        variant={status === "offRoute" ? "error" : "positive"}
        data-testid="driver-overview-route-status"
        data-status={status}
      >
        {status === "offRoute" ? (
          <>
            <CircleAlert className="h-3 w-3" aria-hidden />
            {t("chat.driverOverview.offRoute")}
          </>
        ) : (
          <>
            <Eye className="h-3 w-3" aria-hidden />
            {t("chat.driverOverview.onRoute")}
          </>
        )}
      </Badge>
    )
  )
}

const HeaderActions = ({
  sendRoute,
  onOpenMap,
}: {
  sendRoute: RouteEditing["sendRoute"]
  onOpenMap?: () => void
}) => {
  const { t } = useTranslation()
  return (
    <>
      {sendRoute && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={sendRoute.onSend}
          disabled={sendRoute.sending}
          data-testid="driver-overview-send-route"
          className="h-7 px-2 text-sm text-muted-foreground"
        >
          {sendRoute.sending
            ? t("chat.driverOverview.preparing")
            : sendRoute.label}
        </Button>
      )}
      {onOpenMap && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onOpenMap}
          data-testid="driver-overview-open-map"
          className="h-7 px-2 text-sm text-muted-foreground"
        >
          {t("chat.driverOverview.map")}
          <ArrowUpRight aria-hidden />
        </Button>
      )}
    </>
  )
}

const RouteAhead = ({ route }: { route: RouteView | null }) => (
  <div
    data-testid="driver-overview-route-ahead"
    className={cn("relative my-1", route && "pl-2")}
  >
    <div className="relative">
      <div
        className={cn(
          "h-1.5 rounded-full",
          route ? "bg-[#4CAF50]" : "bg-gray-200 dark:bg-gray-700",
        )}
      />
      {route?.breakMarker && (
        <BreakMarker
          testId="driver-overview-route-break"
          percent={route.breakMarker.percent}
          tooltip={route.breakMarker.tooltip}
        />
      )}
      {route?.stops.map(
        (stop) =>
          stop.aheadPercent !== null && (
            <RouteStopMarker
              key={stop.id}
              stop={stop}
              percent={stop.aheadPercent}
            />
          ),
      )}
    </div>
    {route && (
      <div
        data-testid="driver-overview-pin"
        data-moving={route.moving}
        className="absolute left-0 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full ring-2 ring-white dark:ring-input"
        style={{ backgroundColor: route.moving ? "#4CAF50" : "#6B7280" }}
        aria-hidden
      >
        {route.moving && (
          <ChevronsRight className="h-3 w-3 text-white" strokeWidth={3} />
        )}
      </div>
    )}
  </div>
)

const RouteSide = ({
  stage,
  stageDetail,
  route,
  onOpenMap,
  routeEditing,
}: {
  stage: Stage
  stageDetail: StageDetail | null
  route: RouteView | null
  onOpenMap?: () => void
  routeEditing?: RouteEditing
}) => {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-[min(100%,20rem)] flex-[2_1_0%] flex-col">
      {/* The actions can't sit inside the open-map button, so the whole
        header lights up from the button's hover instead. */}
      <div className="flex shrink-0 flex-col transition-colors has-[[data-open-map-area]:enabled:hover]:bg-muted/60">
        <div className="flex min-h-7 min-w-0 items-center justify-between gap-3 px-4 pt-3">
          <span
            data-testid="driver-overview-stage"
            className="min-w-0 truncate text-sm font-semibold text-foreground"
          >
            {stage.text}
            {stage.detail && (
              <span className="font-normal">{stage.detail}</span>
            )}
          </span>
          <div className="-my-1 flex shrink-0 items-center gap-1">
            {route && <RouteStatusBadge status={route.status} />}
            <HeaderActions
              sendRoute={routeEditing?.sendRoute}
              onOpenMap={onOpenMap}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenMap}
          disabled={!onOpenMap}
          aria-label={t("chat.driverOverview.openMap")}
          data-open-map-area
          className="flex w-full min-w-0 flex-col gap-2 px-4 pb-3 pt-2 text-left disabled:cursor-default"
        >
          <RouteAhead route={route} />
          {stageDetail && (
            <MaybeTooltip tooltip={stageDetail.tooltip}>
              <p
                data-testid="driver-overview-stage-detail"
                className="truncate text-sm text-muted-foreground"
              >
                {stageDetail.label}:{" "}
                <span className="font-medium text-foreground">
                  {stageDetail.value}
                </span>
              </p>
            </MaybeTooltip>
          )}
        </button>
      </div>
      {routeEditing ? (
        <EditableOverviewStops
          stops={route?.stops ?? []}
          routeEditing={routeEditing}
        />
      ) : (
        <StopList stops={route?.stops ?? []} />
      )}
    </div>
  )
}

const WorkTimeButton = ({
  workTime,
  onOpenWorkTime,
}: {
  workTime: Exclude<WorkTimeView, { kind: "none" }>
  onOpenWorkTime?: () => void
}) => {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpenWorkTime}
      disabled={!onOpenWorkTime}
      aria-label={t("chat.driverOverview.openWorkTime")}
      data-testid="driver-overview-work-time"
      data-stale={workTime.kind === "figures" && workTime.staleLabel !== null}
      className="flex w-full shrink-0 flex-col gap-2 px-4 py-3 text-left transition-colors enabled:hover:bg-muted/60 disabled:cursor-default"
    >
      {workTime.kind === "noTacho" ? (
        <>
          <span className="text-sm text-muted-foreground">
            {t("chat.driverOverview.workTime")}
          </span>
          <p
            data-testid="driver-overview-work-time-empty"
            className="text-sm text-muted-foreground"
          >
            {t("chat.driverOverview.noTachoData")}
          </p>
        </>
      ) : (
        <>
          <div
            className={cn(
              "flex flex-col gap-2",
              workTime.staleLabel && "opacity-50",
            )}
          >
            <div className="flex items-baseline justify-between gap-2 text-sm">
              <span className="shrink-0 whitespace-nowrap text-muted-foreground">
                {t("chat.driverOverview.workTime")}
              </span>
              <span className="min-w-0 truncate text-right text-foreground">
                <Trans
                  i18nKey="chat.driverOverview.timeLeft"
                  values={{ time: workTime.leftLabel }}
                  components={{
                    b: <span className="font-semibold tabular-nums" />,
                  }}
                />
              </span>
            </div>
            <div className="relative my-1">
              <Progress value={workTime.usedPercent} className="h-1.5" />
              {workTime.breakPercent !== null && workTime.breakLabel && (
                <BreakMarker
                  testId="driver-overview-work-time-break"
                  percent={Math.max(0, Math.min(100, workTime.breakPercent))}
                  tooltip={t("chat.driverOverview.breakAt", {
                    time: workTime.breakLabel,
                  })}
                />
              )}
            </div>
            {workTime.breakLabel && (
              <p
                data-testid="driver-overview-break"
                className="truncate text-sm text-muted-foreground"
              >
                <Trans
                  i18nKey="chat.driverOverview.breakAtRich"
                  values={{ time: workTime.breakLabel }}
                  components={{
                    b: <span className="font-medium text-foreground" />,
                  }}
                />
              </p>
            )}
          </div>
          {workTime.staleLabel && (
            <p className="truncate text-xs text-muted-foreground">
              {workTime.staleLabel}
            </p>
          )}
        </>
      )}
    </button>
  )
}

type Documents = {
  documents: OrderDocument[]
  onOpenDocument?: (document: OrderDocument) => void
  orderStatus?: OrderStatus
}

const DocumentsSection = ({
  documents,
  onOpenDocument,
  orderStatus,
}: Documents) => {
  // Order status (showsOrderStatus: SLT vhosts with stops) opens this same
  // order, so the order file is not listed again under it. Where that link
  // is absent, the file stays — it is the only way to open the order.
  const { t } = useTranslation()
  const listed = orderStatus
    ? documents.filter((document) => document.kind !== "order")
    : documents
  return (
    <div
      data-testid="driver-overview-documents"
      className="flex flex-col gap-1.5 px-4 py-3"
    >
      <span className="text-sm text-muted-foreground">
        {t("chat.driverOverview.documents")}
      </span>
      {orderStatus && <OrderStatusLink {...orderStatus} />}
      {listed.length > 0 && (
        <OrderDocumentList documents={listed} onOpen={onOpenDocument} />
      )}
    </div>
  )
}

const SideColumn = ({
  workTime,
  onOpenWorkTime,
  documents,
  onOpenDocument,
  orderStatus,
}: Documents & {
  workTime: WorkTimeView
  onOpenWorkTime?: () => void
}) => {
  const hasWorkTime = workTime.kind !== "none"
  const hasDocuments = documents.length > 0 || orderStatus !== undefined
  if (!hasWorkTime && !hasDocuments) return null
  return (
    // Wraps under the route when the card is too narrow for both. Card
    // clips whichever border lands on its edge, so the divider is the left
    // border beside the route and the top border under it.
    <div className="-ml-px -mt-px flex min-w-[min(100%,14rem)] flex-[1_1_0%] flex-col border-l border-t border-border">
      {hasWorkTime && (
        <WorkTimeButton workTime={workTime} onOpenWorkTime={onOpenWorkTime} />
      )}
      {hasWorkTime && hasDocuments && (
        <div className="mx-4 h-px shrink-0 bg-border" aria-hidden />
      )}
      {hasDocuments && (
        <DocumentsSection
          documents={documents}
          onOpenDocument={onOpenDocument}
          orderStatus={orderStatus}
        />
      )}
    </div>
  )
}

const Card = ({
  state,
  className,
  children,
}: {
  state: DriverOverview["kind"]
  className?: string
  children: ReactNode
}) => (
  <div
    data-testid="driver-overview-bar"
    data-state={state}
    className={cn(
      "flex flex-wrap overflow-hidden rounded-lg border border-input bg-white shadow-sm dark:bg-input/30",
      className,
    )}
  >
    {children}
  </div>
)

export type DriverOverviewBarProps = {
  overview: DriverOverview
  onOpenMap?: () => void
  onOpenWorkTime?: () => void
  routeEditing?: RouteEditing
  documents?: OrderDocument[]
  onOpenDocument?: (document: OrderDocument) => void
  orderStatus?: OrderStatus
  className?: string
}

const NO_WORK_TIME: WorkTimeView = { kind: "none" }

export const DriverOverviewBar = ({
  overview,
  onOpenMap,
  onOpenWorkTime,
  routeEditing,
  documents = [],
  onOpenDocument,
  orderStatus,
  className,
}: DriverOverviewBarProps) => {
  switch (overview.kind) {
    case "hidden":
      return null
    case "syncing":
      return (
        <Card state="syncing" className={className}>
          <div
            data-testid="driver-overview-skeleton"
            className="flex flex-1 flex-col gap-3 px-4 py-3"
          >
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-1.5 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        </Card>
      )
    case "noRoute":
      return (
        <Card state="noRoute" className={className}>
          <RouteSide
            stage={overview.stage}
            stageDetail={overview.stageDetail}
            route={null}
            onOpenMap={onOpenMap}
            routeEditing={routeEditing}
          />
          <SideColumn
            workTime={NO_WORK_TIME}
            documents={documents}
            onOpenDocument={onOpenDocument}
            orderStatus={orderStatus}
          />
        </Card>
      )
    case "route":
      return (
        <Card state="route" className={className}>
          <RouteSide
            stage={overview.route.stage}
            stageDetail={overview.route.stageDetail}
            route={overview.route}
            onOpenMap={onOpenMap}
            routeEditing={routeEditing}
          />
          <SideColumn
            workTime={overview.workTime}
            onOpenWorkTime={onOpenWorkTime}
            documents={documents}
            onOpenDocument={onOpenDocument}
            orderStatus={orderStatus}
          />
        </Card>
      )
  }
}

export default DriverOverviewBar
